import { useCallback, useEffect, useState } from "react";
import { adminApi } from "../../lib/api";
import { toast } from "sonner";
import { Loader2, ChevronDown, Trash2 } from "lucide-react";

const STATUS_LABELS = {
  pending: { label: "En attente", color: "bg-amyu-mango text-amyu-ink" },
  confirmed: { label: "Confirmée", color: "bg-amyu-sky text-amyu-ink" },
  ready: { label: "Prête", color: "bg-amyu-pistachio text-amyu-ink" },
  completed: { label: "Livrée", color: "bg-amyu-cream text-amyu-muted" },
  cancelled: { label: "Annulée", color: "bg-amyu-pink text-coral" },
};

const formatEuro = (v) =>
  typeof v === "number" ? `${v.toFixed(2).replace(".", ",")} €` : "—";

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [expanded, setExpanded] = useState(null);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminApi.get("/admin/orders", { params: { status: filter } });
      setOrders(r.data.orders);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const updateStatus = async (id, status) => {
    try {
      await adminApi.patch(`/admin/orders/${id}/status`, { status });
      toast.success("Statut mis à jour");
      fetchOrders();
    } catch (e) {
      toast.error("Erreur de mise à jour");
    }
  };

  const deleteOrder = async (e, o) => {
    e.stopPropagation();
    if (!window.confirm(`Supprimer définitivement la commande #${o.ref || o.id.slice(0, 6).toUpperCase()} ?\nCette action est irréversible.`)) return;
    try {
      await adminApi.delete(`/admin/orders/${o.id}`);
      toast.success("Commande supprimée");
      fetchOrders();
    } catch {
      toast.error("Erreur de suppression");
    }
  };

  const deleteAll = async () => {
    if (
      !window.confirm(
        `⚠️ Supprimer TOUTES les commandes (${orders.length}) ?\nCette action est irréversible et supprime définitivement l'historique.`
      )
    )
      return;
    try {
      const r = await adminApi.delete("/admin/orders");
      toast.success(`${r.data.deleted_count} commande(s) supprimée(s)`);
      fetchOrders();
    } catch {
      toast.error("Erreur de suppression en masse");
    }
  };

  return (
    <div data-testid="admin-orders-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-4xl">Commandes</h1>
          <p className="text-amyu-muted mt-2">Gère le cycle de vie des commandes.</p>
        </div>
        {orders.length > 0 && (
          <button
            onClick={deleteAll}
            data-testid="admin-orders-delete-all"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm bg-white border border-coral/40 text-coral hover:bg-coral hover:text-white transition-colors"
          >
            <Trash2 size={14} /> Tout supprimer
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-2 mt-6" data-testid="admin-orders-filters">
        {["all", ...Object.keys(STATUS_LABELS)].map((s) => (
          <button
            key={s}
            data-testid={`admin-filter-${s}`}
            onClick={() => setFilter(s)}
            className={`pill px-4 py-2 text-sm transition-colors ${
              filter === s
                ? "bg-coral text-white"
                : "bg-white text-amyu-ink border border-amyu hover:border-coral"
            }`}
          >
            {s === "all" ? "Toutes" : STATUS_LABELS[s].label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="animate-spin text-coral" />
        </div>
      ) : orders.length === 0 ? (
        <div className="text-center text-amyu-muted py-20 bg-white rounded-[28px] mt-6">
          Aucune commande pour ce filtre.
        </div>
      ) : (
        <div className="mt-6 space-y-3" data-testid="admin-orders-list">
          {orders.map((o) => {
            const status = STATUS_LABELS[o.status] || STATUS_LABELS.pending;
            const open = expanded === o.id;
            return (
              <article
                key={o.id}
                data-testid={`admin-order-${o.ref || o.id.slice(0, 6)}`}
                className="bg-white rounded-[20px] p-5 shadow-[0_4px_20px_rgba(42,36,34,0.04)]"
              >
                <div
                  className="flex flex-wrap items-center gap-3 cursor-pointer"
                  onClick={() => setExpanded(open ? null : o.id)}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="font-display text-lg">#{o.ref || o.id.slice(0, 6).toUpperCase()}</span>
                      <span className={`pill px-3 py-0.5 text-[11px] uppercase tracking-[0.16em] font-medium ${status.color}`}>
                        {status.label}
                      </span>
                    </div>
                    <div className="text-sm mt-1">
                      <span className="font-medium">{o.customer_name}</span>
                      <span className="text-amyu-muted"> — {o.email}</span>
                    </div>
                    <div className="text-xs text-amyu-muted mt-0.5">
                      {new Date(o.created_at).toLocaleString("fr-FR")} · {o.items.length} article{o.items.length > 1 ? "s" : ""}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-display text-2xl text-coral">{formatEuro(o.subtotal)}</div>
                  </div>
                  <ChevronDown size={18} className={`transition-transform ${open ? "rotate-180" : ""}`} />
                </div>

                {open && (
                  <div className="mt-5 pt-5 border-t border-amyu/20" data-testid={`admin-order-details-${o.ref || o.id.slice(0, 6)}`}>
                    <div className="grid md:grid-cols-2 gap-5">
                      <div>
                        <div className="text-xs uppercase tracking-widest text-amyu-muted">Produits</div>
                        <ul className="mt-2 space-y-1.5 text-sm">
                          {o.items.map((it, i) => (
                            <li key={`${it.product_id}-${i}`} className="flex justify-between">
                              <span>
                                {it.name} <span className="text-amyu-muted">× {it.quantity}</span>
                              </span>
                              <span className="font-medium">{formatEuro(it.line_total)}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        {o.phone && (
                          <>
                            <div className="text-xs uppercase tracking-widest text-amyu-muted">Téléphone</div>
                            <div className="text-sm mt-1">{o.phone}</div>
                          </>
                        )}
                        {o.notes && (
                          <>
                            <div className="text-xs uppercase tracking-widest text-amyu-muted mt-3">Notes</div>
                            <div className="text-sm mt-1 bg-amyu-vanilla rounded-2xl p-3">{o.notes}</div>
                          </>
                        )}
                      </div>
                    </div>

                    <div className="mt-5 flex flex-wrap gap-2">
                      {Object.entries(STATUS_LABELS).map(([key, val]) => (
                        <button
                          key={key}
                          data-testid={`admin-order-set-${key}-${o.ref || o.id.slice(0, 6)}`}
                          onClick={() => updateStatus(o.id, key)}
                          disabled={o.status === key}
                          className={`pill px-4 py-1.5 text-xs uppercase tracking-[0.16em] font-medium transition-colors ${
                            o.status === key
                              ? "bg-amyu-cream text-amyu-muted cursor-default"
                              : "bg-white border border-amyu hover:bg-coral hover:text-white hover:border-coral"
                          }`}
                        >
                          {val.label}
                        </button>
                      ))}
                      <button
                        onClick={(e) => deleteOrder(e, o)}
                        data-testid={`admin-order-delete-${o.ref || o.id.slice(0, 6)}`}
                        className="ml-auto pill px-4 py-1.5 text-xs uppercase tracking-[0.16em] font-medium inline-flex items-center gap-1.5 bg-white border border-coral/40 text-coral hover:bg-coral hover:text-white transition-colors"
                      >
                        <Trash2 size={12} /> Supprimer
                      </button>
                    </div>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}
